import { useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { HOTKEY_SCOPES, HotkeyScope } from './scopes';
import { isSequenceInProgress } from './sequenceGuard';

export interface ActionHotkeyOptions {
  /** Disable the binding without unmounting the caller. Default `true`. */
  enabled?: boolean;
  /** Scope the binding lives in. Default `global` (suppressed when overlays open). */
  scope?: HotkeyScope;
  /** Call `event.preventDefault()` before the handler. Default `true`. */
  preventDefault?: boolean;
  /**
   * Fire while focus is inside a form control. Default `false` — this is the
   * free input-protection win that replaces every hand-rolled `tagName` guard.
   */
  enableOnFormTags?: boolean;
  /**
   * @deprecated No longer needed for handler freshness. The handler is now
   * ref-stabilized internally (stored in a ref updated every render and invoked
   * via `ref.current`), so the latest closure always runs regardless of deps —
   * callers no longer have to enumerate every closed-over value. Retained only
   * for back-compat with existing call sites; the value is ignored.
   */
  deps?: unknown[];
}

/**
 * Declarative wrapper for "do an action" shortcuts (Alt+letter, Cmd+C, …).
 *
 * Bakes in the house defaults: input-protected, preventDefault on, scoped to
 * `global`. react-hotkeys-hook handles listener registration + automatic
 * cleanup, so there are no stale listeners and no manual document wiring.
 */
export function useActionHotkey(
  keys: string | string[],
  handler: (event: KeyboardEvent) => void,
  opts: ActionHotkeyOptions = {}
): void {
  const {
    enabled = true,
    scope = HOTKEY_SCOPES.global,
    preventDefault = true,
    enableOnFormTags = false,
  } = opts;

  // Ref-stabilize the handler so the LATEST closure always runs. rhk v5 freezes
  // the callback whenever a deps array is passed (it `useCallback`s the handler
  // keyed on deps and invokes the frozen ref), so a caller that omits a
  // closed-over value from `deps` would silently run a stale handler. By passing
  // NO deps to `useHotkeys` and routing through a ref we update every render, the
  // binding re-registers only when keys/options change while the handler stays
  // current — callers no longer have to manage `deps` for freshness.
  // Latest-ref pattern: writing `.current` during render is the intended way to
  // keep the ref pointed at the freshest closure (read only later, inside the
  // keydown handler — never during this render). Same exemption pattern as
  // useBulkSelection/useStableValue.
  const handlerRef = useRef(handler);
  // eslint-disable-next-line react-hooks/refs
  handlerRef.current = handler;

  // Gate against the `g`-prefix sequence collision. A `g>x` go-to sequence and
  // a bare-letter action both attach independent `document` keydown listeners,
  // so the sequence's terminal key (e.g. `g` then `d`) would otherwise ALSO fire
  // the bare `d` action. While a sequence is mid-flight, bail before invoking
  // the action. See `sequenceGuard.ts` for why the flag is module-level.
  // NOTE: v5 fires `preventDefault` before the callback, so the terminal key's
  // browser default stays prevented even when we bail — harmless for bare
  // letters; we deliberately do not try to undo it.
  const guardedHandler = (event: KeyboardEvent): void => {
    if (isSequenceInProgress()) return;
    handlerRef.current(event);
  };

  // No deps passed: rhk refreshes its internal callback ref to `guardedHandler`
  // every render, and `guardedHandler` always reads the latest `handlerRef`.
  useHotkeys(keys, guardedHandler, {
    enabled,
    preventDefault,
    enableOnFormTags,
    scopes: [scope],
  });
}
